<template>
  <div class="notification-list">
    <div v-for="item in displayedNotifications" :key="item.id" class="notification-item">
      <div class="notification-content" v-on:click="itemClicked(item)">
        <v-icon v-if="item.code === 'NM14'" size="14" class="pr-1">
          mdi-alert-circle-outline
        </v-icon>

        <!-- Special case for downloading multiple files as archive -->
        <span v-if="item.code === 'NM52'"
              v-html="$replaceT(item.code, processDownloadNotificationParameters(item.params), item.content)"
        />
        <!-- Special case for downloading converted image file -->
        <span v-else-if="item.code === 'NM57'"
              v-html="$replaceT(item.code, processConvertImageResultParameters(item.params), item.content)"
        />
        <span v-else-if="item.podid">
          <router-link :to="{ path: '/pod/' + item.podid }">
            {{ $replaceT(item.code, item.params, item.content) }}
          </router-link>
        </span>
        <span v-else-if="item.nodeid">
          <router-link :to="{ path: '/node/' + item.nodeid }">
            {{ $replaceT(item.code, item.params, item.content) }}
          </router-link>
        </span>
        <span v-else>
          {{ $replaceT(item.code, item.params, item.content) }}
        </span>
      </div>
      <div class="notification-date">
        {{ timeAgo(item.createdat) }}
      </div>
    </div>
    <div v-if="hasMoreNotifications" class="notification-load-more">
      <v-btn block elevation="0" color="secondaryButtons" @click="loadMoreNotifications">
        {{ $t('routes.notifications.loadMoreNotifications') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { useNotificationStore } from '@/stores/NotificationStore.js'
import { useMainStore } from '@/stores/MainStore.js'


export default {
  name: "NotificationsList",
  components: {
    // NotificationsSelectBox
  },
  data: function() {
    return {
      displayedNotifications: [], // Array to store currently displayed notifications
      perPage: 5, // Number of notifications to display per page
      currentPage: 1,
    };
  },
  watch: {
    notifications: function(newValue, oldValue) {
      if ((newValue.length !== oldValue.length) || (this.displayedNotifications.length === 0 && newValue.length > 0)) {
        this.displayedNotifications = this.notifications.slice(0, this.perPage);
        this.currentPage = 1;
      }
    }
  },
  methods: {
    processDownloadNotificationParameters: function(params) {
      const parsedParams = JSON.parse(params);
      return {
        p1: parsedParams.p1,
        p2: new Date(parsedParams.p2).toLocaleString(),
        p3: parsedParams.p3,
      }
    },
    processConvertImageResultParameters: function(params) {
      const parsedParams = JSON.parse(params);
      return {
        p1: parsedParams.p1,
        p2: parsedParams.p2,
      }
    },
    itemClicked: function(item) {
      // check the item code and trigger an action
      const limitsCodes = [
        "reached-users-limit", "reached-clientuser-limit",
        "reached-storage-limit", "reached-bandwidth-limit",
        "reached-translation-limit", "reached-transcription-limit",
        "reached-rekognition-limit", "reached-credits-limit",
        "reached-resource-limit",
      ];

      if (limitsCodes.indexOf(item.code) >= 0) {
        this.showLimitsOnClick()
      }
    },

    showLimitsOnClick: function() {
      if (!this.$canControlTenantConfig()) {
        return;
      }

      const mainStore = useMainStore();
      mainStore.showSubscriptionContactDialog();
    },

    loadMoreNotifications() {
      const start = this.currentPage * this.perPage;
      const end = start + this.perPage;

      this.displayedNotifications.push(...this.notifications.slice(start, end));
      this.currentPage++;
    },
    timeAgo(value) {
      if (!value) {
        return '';
      }

      const date = new Date(value);

      if (isNaN(date.getTime())) {
        return "Invalid date";
      }

      const seconds = Math.floor((new Date() - date) / 1000);

      let interval = seconds / 31536000;  // Number of seconds in a year
      if (interval >= 1) {
        const years = Math.floor(interval);
        return this.$t('routes.notifications.yearsAgo', years);
      }

      interval = seconds / 2592000;  // Number of seconds in a month
      if (interval >= 1) {
        const months = Math.floor(interval);
        return this.$t('routes.notifications.monthsAgo', months);
      }

      interval = seconds / 86400;  // Number of seconds in a day
      if (interval >= 1) {
        const days = Math.floor(interval);
        return this.$t('routes.notifications.daysAgo', days);
      }

      interval = seconds / 3600;  // Number of seconds in an hour
      if (interval >= 1) {
        const hours = Math.floor(interval);
        return this.$t('routes.notifications.hoursAgo', hours);
      }

      interval = seconds / 60;  // Number of seconds in a minute
      if (interval >= 1) {
        const minutes = Math.floor(interval);
        return this.$t('routes.notifications.minutesAgo', minutes);
      }

      return this.$t('routes.notifications.secondsAgo', seconds < 0 ? 2 : seconds);
    },
  },
  computed: {
    notifications: function() {
      const notificationStore = useNotificationStore();
      return notificationStore.getNotifications;
    },
    hasMoreNotifications() {
      return this.notifications.length > this.displayedNotifications.length;
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-list {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.notification-item {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border-radius: 4px; /* Rounded corners for a box-like appearance */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
  margin-bottom: 1rem; /* Maintain spacing between notifications */

  .v-theme--light & {
    background-color: #e8e8e8;
  }

  .v-theme--dark & {
    background-color: #393939;
  }
}

.notification-content {
  flex-grow: 1;
  line-break: anywhere;
}

.notification-date {
  color: #888;
  font-size: 0.8rem;
  align-self: flex-end;
  font-style: italic;
}

.notification-load-more {
  text-align: center;
  margin-top: 1rem;
}
</style>
